// Merge Customer — pick a target customer to merge the source customer into.
// All of the source customer's invoices get re-pointed to the target, and the
// source customer is then removed. Single Dialog with two internal steps
// (pick target -> confirm) instead of two separate Dialog roots, so there's
// no close/open race between them — search reuses the same
// getCustomerListAction the Customers page itself uses.
import * as React from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "ui/dialog";
import { Button } from "ui/button";
import { Input } from "ui/input";

export interface MergeCustomerDialogProps {
  open: boolean;
  onClose: () => void;
  sourceCustomer: any;
  candidates: any[];
  loading: boolean;
  merging: boolean;
  searchValue: string;
  onSearch: (searchText: string) => void;
  onConfirmMerge: (targetCustomerId: string) => void;
}

export function MergeCustomerDialog({
  open,
  onClose,
  sourceCustomer,
  candidates,
  loading,
  merging,
  searchValue,
  onSearch,
  onConfirmMerge,
}: MergeCustomerDialogProps) {
  const [selectedTarget, setSelectedTarget] = React.useState<any>(null);

  const hasSearched = searchValue.trim().length >= 2;
  const filteredCandidates = hasSearched
    ? (candidates || []).filter((c: any) => c?.customerDetailId !== sourceCustomer?.customerDetailId)
    : [];

  const handleOpenChange = (o: boolean) => {
    if (!o) {
      setSelectedTarget(null);
      onClose();
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        {!selectedTarget ? (
          <>
            <DialogHeader>
              <DialogTitle>Merge {sourceCustomer?.customerName}</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              Select the customer to merge <span className="font-medium">{sourceCustomer?.customerName}</span> into.
              All invoices will be moved to the selected customer.
            </p>
            <Input
              autoFocus
              placeholder="Search by Customer Name / Mobile Number"
              value={searchValue}
              onChange={(e) => onSearch(e.target.value)}
            />
            <div className="max-h-72 overflow-y-auto rounded-md border">
              {!hasSearched && (
                <div className="p-3 text-sm text-muted-foreground">Type at least 2 characters to search.</div>
              )}
              {hasSearched && loading && <div className="p-3 text-sm text-muted-foreground">Searching...</div>}
              {hasSearched && !loading && filteredCandidates.length === 0 && (
                <div className="p-3 text-sm text-muted-foreground">No customers found.</div>
              )}
              {hasSearched && !loading && filteredCandidates.map((item: any) => (
                <button
                  key={item?.customerDetailId}
                  type="button"
                  onClick={() => setSelectedTarget(item)}
                  className="flex w-full flex-col items-start gap-0.5 border-b p-3 text-left last:border-b-0 hover:bg-accent"
                >
                  <span className="text-sm text-foreground">{item?.customerName}</span>
                  <span className="text-xs text-muted-foreground">{item?.mobileNumberList || item?.mobileNumber}</span>
                </button>
              ))}
            </div>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Merge into {selectedTarget?.customerName}?</DialogTitle>
            </DialogHeader>
            <p className="text-sm text-muted-foreground">
              All invoices from <span className="font-medium">{sourceCustomer?.customerName}</span> will be moved to{" "}
              <span className="font-medium">{selectedTarget?.customerName}</span>, and{" "}
              <span className="font-medium">{sourceCustomer?.customerName}</span> will be removed. This cannot be undone.
            </p>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setSelectedTarget(null)} disabled={merging}>
                Back
              </Button>
              <Button variant="destructive" onClick={() => onConfirmMerge(selectedTarget?.customerDetailId)} disabled={merging}>
                {merging && <Loader2 className="h-4 w-4 animate-spin" />}
                Merge
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default MergeCustomerDialog;
